/**
 * "Search has moved to the main menu."
 *
 * A one-time notice for trainers who already knew where Search was. The bottom
 * bar's fourth slot now defaults to Meta, and a shortcut disappearing without
 * explanation reads as a bug or a removal, when it is neither: Search kept its
 * main-menu row and can be put straight back.
 *
 * EXISTING TRAINERS ONLY. Someone who signs up after this shipped never knew
 * the old arrangement, so telling them something moved is noise about a past
 * they were not present for. The cut is the account's creation time against
 * SEARCH_MOVED_AT, which is exact and needs no per-device flag.
 *
 * Shown once, dismissed forever, same rule as every other prompt in here.
 */
import { useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { useLocation } from "wouter";
import { useAuth } from "@/components/AuthProvider";
import { useSession } from "@/components/SessionProvider";
import { readBottomSlot } from "@/lib/bottomSlotPref";

const SEEN_KEY = "nx.searchMoved.seen.v1";

/**
 * When the swap shipped. An account created before this knew the old bar;
 * one created after never did.
 */
export const SEARCH_MOVED_AT = Date.parse("2026-08-19T00:00:00Z");

/** Long enough not to collide with the launch, short enough to be seen. */
const DELAY_MS = 6_000;

export function SearchMovedNotice(): JSX.Element | null {
  const { account } = useAuth();
  const { paywallOpen, legalOpen, onboardingOpen, nexQuestionnaireOpen, trainerStepOpen } = useSession();
  const [, setLocation] = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!account) return;
    // Only for trainers who were here before the swap.
    const createdAt = account.createdAt ? Date.parse(String(account.createdAt)) : NaN;
    if (!Number.isFinite(createdAt) || createdAt >= SEARCH_MOVED_AT) return;
    // Nothing to explain if they have already put Search back.
    if (readBottomSlot() === "search") return;
    try {
      if (localStorage.getItem(SEEN_KEY)) return;
    } catch {
      return;
    }
    const timer = setTimeout(() => setVisible(true), DELAY_MS);
    return () => clearTimeout(timer);
  }, [account]);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(SEEN_KEY, "1");
    } catch {
      /* at worst it says it once more. */
    }
  };

  const anyModalOpen = paywallOpen || legalOpen || onboardingOpen || nexQuestionnaireOpen || trainerStepOpen;
  if (!visible || anyModalOpen) return null;

  return (
    <div
      className="fixed left-3 right-3 z-50 rounded-xl p-3 mx-auto"
      style={{
        bottom: "calc(var(--nx-safe-bottom) + 76px)",
        background: "var(--surface-overlay)",
        border: "1px solid var(--accent-fill-glow-subtle)",
        boxShadow: "0 8px 30px rgba(0,0,0,0.28)",
        maxWidth: 460,
      }}
      role="dialog"
      aria-label="Search has moved"
      data-testid="search-moved-notice"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="search-moved-dismiss"
        className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center"
        style={{ color: "var(--text-secondary)" }}
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <p className="text-[13px] font-bold pr-7 flex items-center gap-1.5" style={{ color: "var(--text-primary)" }}>
        <Search className="w-3.5 h-3.5 shrink-0" aria-hidden />
        Search has moved to the main menu
      </p>
      <p className="mt-1 text-[11.5px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
        Meta has the bottom bar slot now. If you want Search back down there, you can switch it
        in Settings.
      </p>

      <div className="mt-2.5 flex gap-2">
        <button
          type="button"
          onClick={() => {
            dismiss();
            setLocation("/settings");
          }}
          data-testid="search-moved-settings"
          className="rounded-full px-3 py-1.5 text-[11.5px] font-bold"
          style={{ background: "var(--accent-fill)", color: "var(--text-on-accent)" }}
        >
          Open Settings
        </button>
        <button
          type="button"
          onClick={dismiss}
          data-testid="search-moved-ok"
          className="rounded-full px-3 py-1.5 text-[11.5px] font-bold"
          style={{
            background: "var(--surface-sunken)",
            color: "var(--text-secondary)",
            border: "1px solid var(--border-subtle)",
          }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}
